import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Github, Send, CheckCircle, AlertCircle } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

export function ContactSophisticated() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!formData.message.trim()) {
      errors.message = 'Message is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://getform.io/f/apjnxrwa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to submit form');

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setFormErrors({});
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <section id="contact" className="relative py-40 md:py-48 px-6 overflow-hidden">
      {/* Subtle background gradient - NOT a box */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-t from-orange-50/30 via-transparent to-transparent" />
        
        {/* Organic blob shapes */}
        <svg 
          className="absolute bottom-0 left-0 w-full h-96 text-gray-50" 
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path 
            fill="currentColor" 
            fillOpacity="0.3" 
            d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
        
      </div>
      
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-light text-gray-900 dark:text-white mb-6">
            Let's Get in Touch
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Whether you have questions about my projects or just want to connect, I'd love to hear from you!
          </p>
        </motion.div>
        
        {/* Split layout - no harsh container */}
        <div className="grid md:grid-cols-2 gap-16">
          {/* Direct contact options */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Social links as cards */}
            <div className="space-y-4">
              <h3 className="text-2xl font-light text-gray-900 dark:text-white mb-8">
                Connect with me
              </h3>
              
              <a 
                href="https://www.linkedin.com/in/seanlund" 
                target="_blank"
                rel="noopener noreferrer"
                className="group block p-6 rounded-2xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-100 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Connect on</p>
                    <p className="text-lg text-gray-900 dark:text-white font-light">LinkedIn</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">/in/seanlund</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/50 flex items-center justify-center transition-colors">
                    <Linkedin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </a>

              <a 
                href="https://github.com/0xSlund" 
                target="_blank"
                rel="noopener noreferrer"
                className="group block p-6 rounded-2xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-100 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Code on</p>
                    <p className="text-lg text-gray-900 dark:text-white font-light">GitHub</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Check out my activity</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800/50 group-hover:bg-gray-200 dark:group-hover:bg-gray-700/50 flex items-center justify-center transition-colors">
                    <Github className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </div>
                </div>
              </a>

              <a 
                href="https://x.com/0xslund" 
                target="_blank"
                rel="noopener noreferrer"
                className="group block p-6 rounded-2xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-100 dark:border-gray-700 hover:border-black dark:hover:border-white hover:bg-gray-50/50 dark:hover:bg-gray-900/20 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Follow on</p>
                    <p className="text-lg text-gray-900 dark:text-white font-light">X</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">@0xslund</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800/50 group-hover:bg-black dark:group-hover:bg-white flex items-center justify-center transition-colors">
                    {/* X Logo SVG */}
                    <svg 
                      className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-white dark:group-hover:text-black transition-colors" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                    >
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                </div>
              </a>

              <a 
                href="https://substack.com/@0xslund" 
                target="_blank"
                rel="noopener noreferrer"
                className="group block p-6 rounded-2xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-100 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-500 hover:bg-orange-50/50 dark:hover:bg-orange-900/20 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Subscribe to</p>
                    <p className="text-lg text-gray-900 dark:text-white font-light">Substack</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">substack.com/@0xslund</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/50 group-hover:bg-orange-200 dark:group-hover:bg-orange-800/50 flex items-center justify-center transition-colors">
                    <svg 
                      className="w-5 h-5 text-orange-600 dark:text-orange-400" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                    >
                      <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.539 24V10.812H1.46zM22.539 0H1.46v2.836h21.08V0z"/>
                    </svg>
                  </div>
                </div>
              </a>
            </div>
            
          </motion.div>
          
          {/* Quick message form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h3 className="text-2xl font-light text-gray-900 dark:text-white mb-8">
              Send a Quick Message
            </h3>
            
            <form onSubmit={handleSubmit} className="flex flex-col h-full">
              {/* Input fields with spacing */}
              <div className="space-y-4">
                <div className="relative">
                  <label 
                    htmlFor="name"
                    className="block text-lg text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Your Name
                  </label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    disabled={isSubmitting}
                    className={`w-full px-0 py-2 border-0 border-b-2 transition-colors text-gray-900 dark:text-white text-lg outline-none bg-transparent ${
                      formErrors.name
                        ? 'border-red-500 dark:border-red-400'
                        : focusedField === 'name' || formData.name
                        ? 'border-orange-500 dark:border-orange-400'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                    required
                  />
                </div>
                
                <div className="relative">
                  <label 
                    htmlFor="email"
                    className="block text-lg text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Your Email
                  </label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    disabled={isSubmitting}
                    className={`w-full px-0 py-2 border-0 border-b-2 transition-colors text-gray-900 dark:text-white text-lg outline-none bg-transparent ${
                      formErrors.email
                        ? 'border-red-500 dark:border-red-400'
                        : focusedField === 'email' || formData.email
                        ? 'border-orange-500 dark:border-orange-400'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                    required
                  />
                </div>
              </div>
              
              {/* Message box that takes remaining space */}
              <div className="flex-1 relative mt-4">
                <label 
                  htmlFor="message"
                  className="block text-lg text-gray-500 dark:text-gray-400 mb-2"
                >
                  Your Message
                </label>
                <div className="relative">
                  <textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    disabled={isSubmitting}
                    maxLength={400}
                    className={`w-full h-56 p-6 rounded-2xl resize-none text-lg transition-all duration-300 shadow-sm focus:outline-none focus:ring-0 ${
                      formErrors.message
                        ? 'border border-red-500 dark:border-red-400 bg-red-50/50 dark:bg-red-900/10'
                        : focusedField === 'message'
                        ? 'border border-orange-500 dark:border-orange-400 bg-orange-50/30 dark:bg-orange-900/10 shadow-orange-200/50 dark:shadow-orange-800/20'
                        : formData.message
                        ? 'border border-orange-300 dark:border-orange-600 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm'
                        : 'border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm hover:border-gray-300 dark:hover:border-gray-600'
                    } text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                    placeholder=""
                    required
                  />
                  {/* Character counter - bottom right inside */}
                  <div className="absolute bottom-3 right-4">
                    <p className={`text-sm transition-colors ${
                      formData.message.length > 350 
                        ? 'text-orange-600 dark:text-orange-400 font-medium' 
                        : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {formData.message.length}/400
                    </p>
                  </div>
                </div>
                
                {/* Submit button - positioned within message container */}
                <div className="mt-4">
                <button 
                type="submit"
                disabled={isSubmitting || submitStatus === 'success'}
                className={`w-full py-3 px-6 rounded-full font-medium text-base flex items-center justify-center gap-2 transition-all duration-300 transform relative overflow-hidden group ${
                  submitStatus === 'success' 
                    ? 'bg-green-500 text-white border-2 border-green-500 scale-105' 
                    : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-2 border-gray-900 dark:border-white hover:bg-gray-800 dark:hover:bg-gray-100 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]'
                } ${isSubmitting || submitStatus === 'success' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {/* Ripple effect overlay */}
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-full" />
                
                {/* Compact content */}
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : submitStatus === 'success' ? (
                  <>
                    <CheckCircle className="w-4 h-4 animate-bounce" />
                    <span>Message Sent!</span>
                  </>
                ) : submitStatus === 'error' ? (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    <span>Try Again</span>
                  </>
                ) : (
                  <>
                    <span>Send Message</span>
                    <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </>
                )}
                </button>
                </div>
              </div>
            </form>
            
            {/* Success/Error Messages */}
            {submitStatus === 'success' && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 text-green-600 dark:text-green-400 text-sm"
              >
                Thanks for reaching out! I'll get back to you soon.
              </motion.p>
            )}
            
            {submitStatus === 'error' && Object.keys(formErrors).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 bg-red-50/50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800 dark:text-red-300 mb-2">Please fix the following errors:</p>
                    <ul className="space-y-1">
                      {Object.entries(formErrors).map(([field, error]) => (
                        error && (
                          <li key={field} className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                            <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                            {error}
                          </li>
                        )
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}
            
            {submitStatus === 'error' && Object.keys(formErrors).length === 0 && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 text-red-600 dark:text-red-400 text-sm"
              >
                Something went wrong. Please try again or email me directly.
              </motion.p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
